
import {Component, Prop, Vue, Watch} from 'vue-property-decorator'
import { exportJson2Excel } from '@/utils/excel'
import { formatJson } from '@/utils'
import Pagination from '@/components/Pagination/index.vue'
import GeneralForm from "@/components/GeneralForm/index.vue";
import configurations from "./configurations/index";
import router from "@/router";
import {entryKey} from "@/components/ListWrapper/configurations/user";

@Component({
  name: 'ComplexTable',
  components: {
    GeneralForm,
    Pagination
  }
})
export default class extends Vue {
  @Prop({ default: false }) private configId! : string;

  private loading = false;

  private tableKey = 0
  private list : {[key: string]: unknown}[] = []
  private total = 0
  private listLoading = true
  private listQuery = {
    startAfter: undefined,
    limit: 10,
    page: 1,
    active: undefined,
    type: undefined,
    sort: undefined,
    language: "EN",
    customerId: null
  }

  private actions : Record<string, any>;
  private columns : Record<string, unknown>;
  private dialogs : Record<string, { tabs: any, activeTab: string; }>;
  private filters : Record<string, unknown>;
  private formValues : Record<string, unknown> = {};
  private getData : (query: any) => any;
  private tutorialLink : string;

  private entryKey : string;
  private submitDialogForm : (params: any) => Promise<void>;

  private dialogId: string = this.$route.query.dialog as string;
  private dialogVisible: boolean = this.dialogId != null;
  private dialogTitle : string = this.$t("table.dialog-title." + this.dialogId).toString();

  private downloadLoading = false

  @Watch('$route')
  private onRouteChange() {
    if (this.$route.query.dialog) {
      this.dialogId = this.$route.query.dialog as string;
      const index = parseInt(this.$route.query.index as string);
      this.formValues[this.dialogId] = this.list[index];
      this.dialogVisible = true;
      this.dialogTitle = this.$t("table.dialog-title." + this.dialogId).toString();
    } else {
      this.dialogVisible = false;
    }
  }

  @Watch('dialogVisible')
  private onDialogVisibleChange() {
    if (!this.dialogVisible) {
      // Remove queries
      this.$router.push(this.$route.path);
    }
  }

  get paginatedList() {
    const pageStartIndex = (this.listQuery.page - 1) * this.listQuery.limit;
    const pageEndIndex = this.listQuery.page * this.listQuery.limit - 1;
    return this.list.filter((item, index) => {
      return pageStartIndex <= index && index <= pageEndIndex;
    })
  }

  get activeTab(){
    return this.dialogs[this.dialogId]?.activeTab || "general";
  }
  set activeTab(value: string) {
    this.dialogs[this.dialogId].activeTab = value;
  }

  created() {
    // Get configuration
    const {actions, columns, dialogs, filters, getData, submitDialogForm, tutorialLink} = configurations[this.configId];

    //Set configuration
    this.actions = actions;
    this.columns = columns;
    this.dialogs = dialogs;
    this.filters = filters;
    this.getData = getData;
    this.tutorialLink = tutorialLink;
    this.submitDialogForm = submitDialogForm;
    this.entryKey = entryKey;

    // Get list
    this.getList()

    // Set event listeners
    this.setEventListeners();
  }

  beforeDestroy(){
    this.removeEventListeners();
  }

  private async submitForm(){
    // Start loading
    this.loading = true;

    // On submit
    if (this.submitDialogForm){
      this.submitDialogForm(this.formValues[this.dialogId as string])
          .then(({data} : any) => {
            // Reinsert data into list
            if (this.entryKey){
              const index = this.list.findIndex((item : any) => item[this.entryKey] ? item[this.entryKey] === data[this.entryKey] : false);
              if (index < 0){
                this.list.splice(index, 1, {...this.list[index], ...data});
              }
            }

            // Notify user
            this.$notify({
              title: this.$t('notifications.' + router.currentRoute.name + '-submit-' + this.dialogId + '.success.title').toString(),
              message: this.$t('notifications.' + router.currentRoute.name + '-submit-' + this.dialogId + '.success.message').toString(),
              type: 'success'
            });

            // Go to return page
            if (router.currentRoute.meta.returnPage || (this.dialogs[this.dialogId] as any).returnPage) {
              this.$router.push((this.dialogs[this.dialogId] as any).returnPage || router.currentRoute.meta.returnPage);
            }
          })
          .catch((error : any) => {
            // Log error
            console.error(error);

            // Notify user
            this.$notify({
              title: this.$t('notifications.' + router.currentRoute.name + '-submit-' + this.dialogId + '.error.title').toString(),
              message: this.$t('notifications.' + router.currentRoute.name + '-submit-' + this.dialogId + '.error.message').toString(),
              type: 'error'
            });

          }).finally(() => {
            // Done
            this.loading = false;
            this.cancelDialog();
          });
    } else {
      // Done
      this.loading = false;
    }
  }

  private async getList() {
    // Start loading
    this.listLoading = true

    // Retrieve product data
    const { data } = await this.getData(this.listQuery);

    // Create list
    if (data.items){
      this.list = data.items
      this.total = data.total
    } else {
      this.list = data;
      this.total = this.list.length;
    }

    // Done
    this.listLoading = false
  }

  private handleFilter() {
    this.listQuery.startAfter = undefined;
    this.getList();
  }

  private handleDownload() {
    this.downloadLoading = true
    const tHeader = ['id', 'created', 'name', 'type', 'active', 'price']
    const filterVal = ['id', 'created', 'name', 'type', 'active', 'price']
    const data = formatJson(filterVal, this.list)
    exportJson2Excel(tHeader, data, 'table-list')
    this.downloadLoading = false
  }

  private handleCommand(command: string, row: any, index: number){
    this.actions[command].click(row, index);
  }

  private startLoading(){
    // Start loading
    this.listLoading = true
  }

  private cancelDialog(){
    // Cancel dialog
    this.dialogVisible = false;
    this.$router.push(this.$route.path);
  }

  private setEventListeners(){
    window.GlobalEvents.$on("refresh-list", this.getList);
    window.GlobalEvents.$on("start-list-load", this.startLoading);
  }

  private removeEventListeners(){
    window.GlobalEvents.$off("refresh-list", this.getList);
    window.GlobalEvents.$on("start-list-load", this.startLoading);
  }
}
